import type { SymbolName } from '../ui'

export type BereichGruppe = 'alltag' | 'familie' | 'finanzen' | 'ueberblick' | 'system'

export interface BereichDefinition {
  id: string
  pfad: string
  label: string
  symbol: SymbolName
  gruppe: BereichGruppe
  braucht: string | readonly string[] | null
  ordnung: number
  /** Weitere Routen, die in der Navigation zu diesem Bereich gehören. */
  unterseiten?: readonly string[]
}

export const BEREICH_GRUPPEN: readonly { id: BereichGruppe; label: string }[] = [
  { id: 'alltag', label: 'Alltag' },
  { id: 'familie', label: 'Familie & Planung' },
  { id: 'finanzen', label: 'Finanzen & Kontakte' },
  { id: 'ueberblick', label: 'Überblick & Regeln' },
  { id: 'system', label: 'System' },
]

/** Eine Quelle für Schale, Mehr-Seite, Suche, Berechtigungen und Aktivmarkierung. */
export const BEREICHE: readonly BereichDefinition[] = [
  {
    id: 'heute',
    pfad: '/',
    label: 'Heute',
    symbol: 'home',
    gruppe: 'alltag',
    braucht: null,
    ordnung: 10,
  },
  {
    id: 'kalender',
    pfad: '/kalender',
    label: 'Kalender',
    symbol: 'calendar',
    gruppe: 'alltag',
    braucht: 'calendar.view',
    ordnung: 20,
  },
  {
    id: 'aufgaben',
    pfad: '/aufgaben',
    label: 'Aufgaben',
    symbol: 'tasks',
    gruppe: 'alltag',
    braucht: 'tasks.view',
    ordnung: 30,
  },
  {
    id: 'einkaufen',
    pfad: '/einkaufen',
    label: 'Einkaufen',
    symbol: 'shopping',
    gruppe: 'alltag',
    braucht: 'shopping.view',
    ordnung: 40,
  },
  {
    id: 'mahlzeiten',
    pfad: '/mahlzeiten',
    label: 'Mahlzeiten',
    symbol: 'meals',
    gruppe: 'familie',
    braucht: 'meals.view',
    ordnung: 50,
  },
  {
    id: 'stundenplan',
    pfad: '/stundenplan',
    label: 'Stundenplan',
    symbol: 'timetable',
    gruppe: 'familie',
    braucht: 'timetable.view',
    ordnung: 60,
  },
  {
    id: 'familie',
    pfad: '/familie',
    label: 'Familie',
    symbol: 'family',
    gruppe: 'familie',
    braucht: 'member.manage',
    ordnung: 70,
    unterseiten: ['/familie/'],
  },
  {
    id: 'taschengeld',
    pfad: '/taschengeld',
    label: 'Taschengeld',
    symbol: 'allowance',
    gruppe: 'finanzen',
    braucht: 'allowance.view',
    ordnung: 80,
  },
  {
    id: 'budget',
    pfad: '/budget',
    label: 'Budget',
    symbol: 'budget',
    gruppe: 'finanzen',
    braucht: 'budget.view',
    ordnung: 90,
  },
  {
    id: 'kontakte',
    pfad: '/kontakte',
    label: 'Kontakte',
    symbol: 'contacts',
    gruppe: 'finanzen',
    braucht: 'contacts.view',
    ordnung: 100,
  },
  {
    id: 'aktivitaet',
    pfad: '/aktivitaet',
    label: 'Aktivität',
    symbol: 'activity',
    gruppe: 'ueberblick',
    braucht: ['tasks.view', 'calendar.view', 'shopping.view', 'meals.view'],
    ordnung: 110,
  },
  {
    id: 'statistiken',
    pfad: '/statistiken',
    label: 'Statistiken',
    symbol: 'stats',
    gruppe: 'ueberblick',
    braucht: 'tasks.view',
    ordnung: 120,
  },
  {
    id: 'automatisierungen',
    pfad: '/automatisierungen',
    label: 'Automatisierungen',
    symbol: 'automation',
    gruppe: 'ueberblick',
    braucht: 'automation.view',
    ordnung: 130,
    unterseiten: ['/automatisierungen/'],
  },
  {
    id: 'einstellungen',
    pfad: '/einstellungen',
    label: 'Einstellungen',
    symbol: 'account',
    gruppe: 'system',
    braucht: null,
    ordnung: 140,
  },
]

export function bereichErlaubt(
  bereich: BereichDefinition,
  caps: ReadonlySet<string> | null | undefined,
): boolean {
  if (bereich.braucht === null) return true
  const benoetigt = typeof bereich.braucht === 'string' ? [bereich.braucht] : bereich.braucht
  return benoetigt.some((capability) => caps?.has(capability) ?? false)
}

export function sichtbareBereiche(
  caps: ReadonlySet<string> | null | undefined,
): BereichDefinition[] {
  return BEREICHE.filter((bereich) => bereichErlaubt(bereich, caps)).sort(
    (a, b) => a.ordnung - b.ordnung,
  )
}

export function bereichFuerPfad(
  pfad: string,
  bereiche: readonly BereichDefinition[] = BEREICHE,
): BereichDefinition | null {
  return (
    bereiche.find((bereich) => {
      if (bereich.pfad === '/') return pfad === '/'
      if (pfad === bereich.pfad || pfad.startsWith(`${bereich.pfad}/`)) return true
      return bereich.unterseiten?.some((praefix) => pfad.startsWith(praefix)) ?? false
    }) ?? null
  )
}

export function bereichMitId(id: string): BereichDefinition {
  const bereich = BEREICHE.find((kandidat) => kandidat.id === id)
  if (!bereich) throw new Error(`Unbekannter Navigationsbereich: ${id}`)
  return bereich
}
